import { Field } from '@base-ui/react/field'

import { cn } from '@/lib/utils'
import { HEX_COLOR } from '@/lib/validators'

import type { FieldProps } from './field-props'
import { Input } from './input'

// 'url' stays a text input: type="url" blocks submit on root-relative
// paths like /logo.svg, which the url fields accept.
function inputType(kind: FieldProps['kind']): 'password' | 'email' | 'text' {
  if (kind === 'password') return 'password'
  if (kind === 'email') return 'email'
  return 'text'
}

export function TextField({
  ref,
  className,
  name,
  label,
  kind,
  hint,
  placeholder,
  autoFocus,
  autoComplete,
  required,
  maxLength,
  onKeyDown,
  value,
  error,
  disabled,
  describedBy,
  onChange,
  onBlur,
}: FieldProps & { className?: string }) {
  return (
    <Field.Root
      name={name}
      data-slot="text-field"
      className={cn('flex flex-col gap-1.5', className)}
    >
      <Field.Label className="text-xs font-medium">{label}</Field.Label>
      <div className="flex items-center gap-2">
        <Field.Control
          {...(autoFocus === undefined ? {} : { autoFocus })}
          ref={ref}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          required={required}
          maxLength={maxLength}
          onKeyDown={onKeyDown}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={error !== undefined ? true : undefined}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          render={<Input type={inputType(kind)} />}
        />
        {kind === 'color' && (
          <span
            aria-hidden
            className="border-border size-6 shrink-0 rounded border"
            style={{
              backgroundColor: HEX_COLOR.test(value) ? value : 'transparent',
            }}
          />
        )}
      </div>
      <Field.Error
        match={error !== undefined}
        role="alert"
        className="text-destructive text-xs"
      >
        {error}
      </Field.Error>
      {hint && (
        <Field.Description className="text-muted-foreground text-xs">
          {hint}
        </Field.Description>
      )}
    </Field.Root>
  )
}
